// src/services/api.js
import axios from 'axios';

// 創建 axios 實例
const api = axios.create({
  baseURL: 'https://pgshophardwarehub.com/backend/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// 請求攔截器 - 添加 JWT Token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 修改：響應攔截器 - 不自動跳轉登入頁（允許未登入狀態）
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 401 錯誤不再自動跳轉，讓各個 API 自行處理
    return Promise.reject(error);
  }
);

// ==================== 認證 API ====================

export const authAPI = {
  // 註冊
  register: async (data) => {
    const response = await api.post('/auth/register', data);
    
    const result = response.data;
    const userData = result.data || result;
    
    if (userData.token) {
      localStorage.setItem('token', userData.token);
      localStorage.setItem('user', JSON.stringify({
        id: userData.userId,
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
      }));
      
      await localCartAPI.syncToServer();
    }
    
    return userData;
  },

  // 登入
  login: async (data) => {
    const response = await api.post('/auth/login', data);
    
    const result = response.data;
    const userData = result.data || result;
    
    if (userData.token) {
      localStorage.setItem('token', userData.token);
      localStorage.setItem('user', JSON.stringify({
        id: userData.userId,
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
      }));
      
      await localCartAPI.syncToServer();
    }
    
    return userData;
  },

  // 獲取當前用戶信息
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    const result = response.data;
    return result.data || result;
  },

  // 登出
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  // 檢查是否已登入
  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  // 獲取當前用戶（從 localStorage）
  getUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }
};

// ==================== 產品 API ====================

export const productAPI = {
  // 獲取熱門商品
  getPopularProducts: async (limit = 10) => {
    try {
      const response = await api.get(`/products/popular?limit=${limit}`);
      const result = response.data;
      return result.data || result;
    } catch (error) {
      console.error('獲取熱門商品失敗:', error);
      throw error;
    }
  },

  // 獲取所有分類
  getCategories: async () => {
    try {
      const response = await api.get('/categories');
      const result = response.data;
      return result.data || result;
    } catch (error) {
      console.error('獲取分類失敗:', error);
      throw error;
    }
  },

  // 獲取所有商品（帶篩選）
  getAllProducts: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/products${queryString ? '?' + queryString : ''}`);
      const result = response.data;
      return result.data || result;
    } catch (error) {
      console.error('獲取商品列表失敗:', error);
      throw error;
    }
  },

  // 獲取單個商品詳情
  getProductById: async (id) => {
    try {
      const response = await api.get(`/products/${id}`);
      const result = response.data;
      return result.data || result;
    } catch (error) {
      console.error('獲取商品詳情失敗:', error);
      throw error;
    }
  },

  // 搜尋商品
  searchProducts: async (keyword) => {
    try {
      const response = await api.get(`/products/search?q=${encodeURIComponent(keyword)}`);
      const result = response.data;
      return result.data || result;
    } catch (error) {
      console.error('搜尋商品失敗:', error);
      throw error;
    }
  },
    // 獲取相關產品
  getRelatedProducts: async (id, limit = 4) => {
    const response = await api.get(`/products/${id}/related?limit=${limit}`);
    const result = response.data;
    return result.data || result;
  },

  // 獲取推薦產品
  getRecommendedProducts: async (id, limit = 4) => {
    const response = await api.get(`/products/${id}/recommended?limit=${limit}`);
    const result = response.data;
    return result.data || result;
  }
};

// ==================== 本地購物車 API（未登入時使用）====================

export const localCartAPI = {
  // 本地儲存鍵名
  CART_KEY: 'guest_cart',
  
  // 修復：獲取本地購物車並強制重新計算 summary
  getCart: () => {
    const cartStr = localStorage.getItem(localCartAPI.CART_KEY);
    const cart = cartStr ? JSON.parse(cartStr) : { items: [] };
    
    // 關鍵修復：每次都重新計算 summary
    localCartAPI.updateSummary(cart);
    
    return cart;
  },
  
  // 保存本地購物車
  saveCart: (cart) => {
    localStorage.setItem(localCartAPI.CART_KEY, JSON.stringify(cart));
  },
  
  // 獲取購物車數量
  getCartCount: () => {
    const cart = localCartAPI.getCart();
    return cart.items.reduce((sum, item) => sum + parseInt(item.quantity || 0), 0);
  },
  
  // 添加到購物車
  addToCart: (productData) => {
    const cart = localCartAPI.getCart();
    
    // 檢查商品是否已存在
    const existingIndex = cart.items.findIndex(
      item => item.productId === productData.productId && 
              item.variant === productData.variant
    );
    
    if (existingIndex !== -1) {
      // 商品已存在，增加數量
      cart.items[existingIndex].quantity += productData.quantity || 1;
    } else {
      // 新商品，添加到購物車
      cart.items.push({
        id: Date.now(),
        ...productData,
        isSelected: true,
        quantity: productData.quantity || 1
      });
    }
    
    localCartAPI.updateSummary(cart);
    localCartAPI.saveCart(cart);
    return cart;
  },
  
  // 更新數量
  updateQuantity: (itemId, quantity) => {
    const cart = localCartAPI.getCart();
    const item = cart.items.find(item => item.id === itemId);
    
    if (item) {
      item.quantity = Math.max(1, parseInt(quantity) || 1);
      localCartAPI.updateSummary(cart);
      localCartAPI.saveCart(cart);
    }
    
    return cart;
  },
  
  // 切換選中狀態
  toggleSelect: (itemId, selected) => {
    const cart = localCartAPI.getCart();
    const item = cart.items.find(item => item.id === itemId);
    
    if (item) {
      item.isSelected = selected;
      localCartAPI.updateSummary(cart);
      localCartAPI.saveCart(cart);
    }
    
    return cart;
  },
  
  // 全選/取消全選
  toggleSelectAll: (selected) => {
    const cart = localCartAPI.getCart();
    cart.items.forEach(item => {
      item.isSelected = selected;
    });
    
    localCartAPI.updateSummary(cart);
    localCartAPI.saveCart(cart);
    return cart;
  },
  
  // 刪除商品
  removeItem: (itemId) => {
    const cart = localCartAPI.getCart();
    cart.items = cart.items.filter(item => item.id !== itemId);
    
    localCartAPI.updateSummary(cart);
    localCartAPI.saveCart(cart);
    return cart;
  },
  
  // 清空購物車
  clearCart: () => {
    const cart = { items: [] };
    localCartAPI.updateSummary(cart);
    localCartAPI.saveCart(cart);
    return cart;
  },
  
  // 刪除選中的商品
  removeSelected: () => {
    const cart = localCartAPI.getCart();
    cart.items = cart.items.filter(item => !item.isSelected);
    
    localCartAPI.updateSummary(cart);
    localCartAPI.saveCart(cart);
    return cart;
  },
  
  // 關鍵修復：更新摘要信息（使用 parseFloat 確保數字計算）
  updateSummary: (cart) => {
    // 計算總件數（所有商品的數量總和）
    const totalItems = cart.items.reduce((sum, item) => {
      const qty = parseInt(item.quantity) || 0;
      return sum + qty;
    }, 0);
    
    // 計算選中商品的件數
    const selectedItems = cart.items
      .filter(item => item.isSelected)
      .reduce((sum, item) => {
        const qty = parseInt(item.quantity) || 0;
        return sum + qty;
      }, 0);
    
    // 關鍵修復：使用 parseFloat 確保價格是數字
    const subtotal = cart.items
      .filter(item => item.isSelected)
      .reduce((sum, item) => {
        const price = parseFloat(item.price) || 0;
        const qty = parseInt(item.quantity) || 0;
        return sum + (price * qty);
      }, 0);
    
    // 計算運費（滿 500 免運費）
    const shipping = subtotal >= 500 ? 0 : 15;
    
    // 計算總計
    const total = subtotal + shipping;
    
    cart.summary = {
      totalItems,
      selectedItems,
      subtotal,
      discount: 0,  // 默認無折扣，優惠券折扣由前端計算
      shipping,
      total
    };
  },
  
  // 同步本地購物車到伺服器（登入時調用）
  syncToServer: async () => {
    const localCart = localCartAPI.getCart();
    
    if (localCart.items.length === 0) {
      return;
    }
    
    try {
      for (const item of localCart.items) {
        await cartAPI.addToCart({
          productId: item.productId,
          quantity: item.quantity,
          variant: item.variant
        });
      }
      
      localCartAPI.clearCart();
      
      console.log('本地購物車已同步到伺服器');
    } catch (error) {
      console.error('同步購物車失敗:', error);
    }
  }
};

// ==================== 購物車 API ====================

export const cartAPI = {
  // 獲取購物車
  getCart: async () => {
    if (!authAPI.isAuthenticated()) {
      return localCartAPI.getCart();
    }
    
    try {
      const response = await api.get('/cart');
      const result = response.data;
      return result.data || result;
    } catch (error) {
      if (error.response?.status === 401) {
        return localCartAPI.getCart();
      }
      throw error;
    }
  },

  // 獲取購物車數量
  getCartCount: async () => {
    if (!authAPI.isAuthenticated()) {
      return localCartAPI.getCartCount();
    }
    
    try {
      const response = await api.get('/cart/count');
      const result = response.data;
      return result.data?.count || result.count || 0;
    } catch (error) {
      if (error.response?.status === 401) {
        return localCartAPI.getCartCount();
      }
      throw error;
    }
  },

  // 添加到購物車
  addToCart: async (productData) => {
    if (!authAPI.isAuthenticated()) {
      return localCartAPI.addToCart(productData);
    }
    
    try {
      const response = await api.post('/cart/add', productData);
      const result = response.data;
      return result.data || result;
    } catch (error) {
      if (error.response?.status === 401) {
        return localCartAPI.addToCart(productData);
      }
      throw error;
    }
  },

  // 更新數量
  updateQuantity: async (id, quantity) => {
    if (!authAPI.isAuthenticated()) {
      return localCartAPI.updateQuantity(id, quantity);
    }
    
    try {
      const response = await api.put(`/cart/${id}/quantity`, { quantity });
      const result = response.data;
      return result.data || result;
    } catch (error) {
      if (error.response?.status === 401) {
        return localCartAPI.updateQuantity(id, quantity);
      }
      throw error;
    }
  },

  // 切換選中狀態
  toggleSelect: async (id, selected) => {
    if (!authAPI.isAuthenticated()) {
      return localCartAPI.toggleSelect(id, selected);
    }
    
    try {
      const response = await api.put(`/cart/${id}/select`, { selected });
      const result = response.data;
      return result.data || result;
    } catch (error) {
      if (error.response?.status === 401) {
        return localCartAPI.toggleSelect(id, selected);
      }
      throw error;
    }
  },

  // 全選/取消全選
  toggleSelectAll: async (selected) => {
    if (!authAPI.isAuthenticated()) {
      return localCartAPI.toggleSelectAll(selected);
    }
    
    try {
      const response = await api.put('/cart/select-all', { selected });
      const result = response.data;
      return result.data || result;
    } catch (error) {
      if (error.response?.status === 401) {
        return localCartAPI.toggleSelectAll(selected);
      }
      throw error;
    }
  },

  // 刪除商品
  removeItem: async (id) => {
    if (!authAPI.isAuthenticated()) {
      return localCartAPI.removeItem(id);
    }
    
    try {
      const response = await api.delete(`/cart/${id}`);
      const result = response.data;
      return result.data || result;
    } catch (error) {
      if (error.response?.status === 401) {
        return localCartAPI.removeItem(id);
      }
      throw error;
    }
  },

  // 清空購物車
  clearCart: async () => {
    if (!authAPI.isAuthenticated()) {
      return localCartAPI.clearCart();
    }
    
    try {
      const response = await api.delete('/cart/clear');
      const result = response.data;
      return result.data || result;
    } catch (error) {
      if (error.response?.status === 401) {
        return localCartAPI.clearCart();
      }
      throw error;
    }
  },

  // 刪除選中的商品
  removeSelected: async () => {
    if (!authAPI.isAuthenticated()) {
      return localCartAPI.removeSelected();
    }
    
    try {
      const response = await api.delete('/cart/selected');
      const result = response.data;
      return result.data || result;
    } catch (error) {
      if (error.response?.status === 401) {
        return localCartAPI.removeSelected();
      }
      throw error;
    }
  }
};

export default api;